import React, { useState, useEffect } from 'react';
import { getMissionTimeline } from '../../../services/positionService';
import './MissionGpsTimeline.css';

const formatDuration = (seconds) => {
    if (seconds == null || seconds < 0) return '0 min';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}h ${m}min`;
    if (m > 0) return `${m}min`;
    return `${s}s`;
};

const formatTime = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const formatDistance = (km) => {
    if (km == null) return '0 km';
    if (km < 1) return `${Math.round(km * 1000)}m`;
    return `${km.toFixed(2)}km`;
};

// Reverse geocode via Mapbox
const reverseGeocode = async (lat, lon) => {
    const token = process.env.REACT_APP_MAPBOX_TOKEN;
    if (!token || !lat || !lon) return null;
    try {
        const res = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?types=address,place&limit=1&access_token=${token}`
        );
        const data = await res.json();
        return data?.features?.[0]?.place_name || null;
    } catch {
        return null;
    }
};

const MissionGpsTimeline = ({ missionId }) => {
    const [segments, setSegments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [addresses, setAddresses] = useState({});
    const [routeContexts, setRouteContexts] = useState({}); // { [idx]: { from: '', to: '' } }

    useEffect(() => {
        if (!missionId) return;
        setLoading(true);
        setError(null);
        getMissionTimeline(missionId)
            .then((data) => {
                setSegments(data);
                data.forEach((seg, idx) => {
                    if (seg.type === 'STOP' && seg.latitude && seg.longitude) {
                        reverseGeocode(seg.latitude, seg.longitude).then((addr) => {
                            if (addr) setAddresses((prev) => ({ ...prev, [idx]: addr }));
                        });
                    } else if (seg.type === 'DRIVING') {
                        // Smart Geocoding: Resolve start and end of path
                        Promise.all([
                            reverseGeocode(seg.latitude, seg.longitude),
                            reverseGeocode(seg.endLatitude, seg.endLongitude)
                        ]).then(([from, to]) => {
                            if (from || to) {
                                setRouteContexts(prev => ({ 
                                    ...prev, 
                                    [idx]: { 
                                        from: from?.split(',')[0] || 'Position inconnue', 
                                        to: to?.split(',')[0] || 'Position inconnue' 
                                    } 
                                }));
                            }
                        });
                    }
                });
            })
            .catch((err) => {
                console.error('Timeline error:', err);
                setError('Impossible de charger la timeline GPS.');
            })
            .finally(() => setLoading(false));
    }, [missionId]);

    const totalDrivingSeconds = segments.filter(s => s.type === 'DRIVING').reduce((acc, s) => acc + (s.durationSeconds || 0), 0);
    const totalStopSeconds = segments.filter(s => s.type === 'STOP').reduce((acc, s) => acc + (s.durationSeconds || 0), 0);
    const totalDistanceKm = segments.filter(s => s.type === 'DRIVING').reduce((acc, s) => acc + (s.distanceKm || 0), 0);

    if (loading) {
        return (
            <div className="hf-timeline-loading py-5 text-center">
                <div className="spinner-border text-primary" role="status"></div>
                <div className="mt-3 fs-6 text-muted fw-bold">Analyse du trajet en cours...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="hf-timeline-error p-4 text-center glass-error">
                <i className="bi bi-exclamation-octagon fs-2 mb-3 d-block"></i>
                <div className="fw-bold">{error}</div>
            </div>
        );
    }

    return (
        <div className="hf-gps-timeline-container">
            {/* Unified Metrics Bar */}
            <div className="hf-timeline-header-glass">
                <div className="metric-pill driving">
                    <i className="bi bi-truck me-2"></i>
                    <span>{formatDuration(totalDrivingSeconds)}</span>
                </div>
                <div className="metric-pill distance">
                    <i className="bi bi-geo-alt me-2"></i>
                    <span>{formatDistance(totalDistanceKm)}</span>
                </div>
                <div className="metric-pill stop">
                    <i className="bi bi-p-circle me-2"></i>
                    <span>{formatDuration(totalStopSeconds)}</span>
                </div>
            </div>

            {/* Scrolling Timeline Chronicle */}
            <div className="hf-timeline-chronicle">
                {segments.map((seg, idx) => (
                    <div key={idx} className={`hf-segment-card ${seg.type.toLowerCase()}`}>
                        
                        {/* Vertical Timeline Marker */}
                        <div className="hf-marker-col">
                            <div className="hf-marker-line"></div>
                            <div className={`hf-status-icon ${seg.type.toLowerCase()}`}>
                                {seg.type === 'DRIVING' ? <i className="bi bi-cursor-fill"></i> : <i className="bi bi-geo-fill"></i>}
                            </div>
                        </div>

                        {/* Content Area */}
                        <div className="hf-segment-body">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="hf-segment-time">{formatTime(seg.startTime)}</span>
                                <span className={`hf-badge-status ${seg.type.toLowerCase()}`}>
                                    {seg.type === 'DRIVING' ? 'EN ROUTE' : 'ARRÊT'}
                                </span>
                            </div>

                            {/* Main Info Context */}
                            <div className="hf-segment-main-info">
                                {seg.type === 'DRIVING' ? (
                                    <div className="hf-route-narrative">
                                        {routeContexts[idx] ? (
                                            <>De <span className="loc">{routeContexts[idx].from}</span> ➔ Vers <span className="loc">{routeContexts[idx].to}</span></>
                                        ) : (
                                            <span className="text-muted italic">Déplacement en cours...</span>
                                        )}
                                    </div>
                                ) : (
                                    <div className="hf-stop-address">
                                        <i className="bi bi-geo-alt-fill me-2 text-primary"></i>
                                        {addresses[idx] || 'Recherche de l\'adresse...'}
                                    </div>
                                )}
                            </div>

                            {/* Secondary Metrics */}
                            <div className="hf-segment-metrics mt-2">
                                <div className="hf-metric-item">
                                    <i className="bi bi-clock me-1"></i> {formatDuration(seg.durationSeconds)}
                                </div>
                                {seg.type === 'DRIVING' && (
                                    <>
                                        <div className="hf-metric-item">
                                            <i className="bi bi-activity me-1"></i> {formatDistance(seg.distanceKm)}
                                        </div>
                                        <div className="hf-metric-item highlights">
                                            <i className="bi bi-speedometer2 me-1"></i> Max {Math.round(seg.maxSpeed || 0)}km/h
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default MissionGpsTimeline;
